/* eslint-disable no-console */
import {
  EventEmitter,
  Injectable,
  OnDestroy,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { Store } from '@ngrx/store';
import * as Sentry from '@sentry/angular';
import type * as SpeechSDKType from 'microsoft-cognitiveservices-speech-sdk/distrib/lib/microsoft.cognitiveservices.speech.sdk';
import { environment } from '../../../../../../environments/environment';
import * as fromFeatureFlags from '../../../../../feature-flags';
import {
  SpeechToTextApiKeyServiceInterface,
  SpeechToTextServiceInterface,
  SpeechToTextServiceStateType,
  SpeechToTextStreamingResult,
  SpeechToTextTranscriptionResult,
} from '../prompt-input-speech-to-text.interfaces';
import * as fromHelper from '../speech-to-text.helper';
import { SpeechToTextAzureApiKeyService } from './speech-to-text-azure-api-key.service';
import {
  AudioConfig,
  AutoDetectSourceLanguageConfig,
  CancellationErrorCode,
  CancellationReason,
  ResultReason,
  SpeechConfig,
  SpeechRecognizer,
} from './speech-to-text-azure-speech-sdk';

@Injectable()
export class SpeechToTextAzureService
  implements SpeechToTextServiceInterface, OnDestroy
{
  private readonly azureApiKeyService =
    inject<SpeechToTextApiKeyServiceInterface>(SpeechToTextAzureApiKeyService);
  private readonly featureFlagsStore = inject(
    Store<fromFeatureFlags.FeatureFlagsState>,
  );
  private readonly ffSttDebugLog = this.featureFlagsStore.selectSignal(
    fromFeatureFlags.featureFlagsFeature.selectFeatureFlag<boolean>(
      'ff-stt-debug-log',
    ),
  );

  /** This is the transcription chunks in process. It is filled with the transcription chunks as they are returned from the azure speech-to-text sdk. */
  private transcriptionChunksInProcess: string[] = [];

  public readonly state = signal<SpeechToTextServiceStateType>('inactive');
  public readonly transcriptionCompleted =
    new EventEmitter<SpeechToTextTranscriptionResult>();
  public readonly streamResult =
    new EventEmitter<SpeechToTextStreamingResult>();

  private recognizer: SpeechSDKType.SpeechRecognizer | undefined;
  private mediaStream: MediaStream | undefined;

  constructor() {
    effect(() => {
      switch (this.state()) {
        case 'inactive':
          this.transcriptionChunksInProcess = [];
          break;
        case 'starting':
          void this.initializeAudioStreaming()
            .then(() => this.initializeRecognizer())
            .then(() => this.startContinuousRecognition());
          break;
        case 'finalizing':
          this.stopAzureRecognizer();
          break;
        case 'completed':
          this.transcriptionCompleted.emit({
            raw: this.transcriptionChunksInProcess,
            clean: fromHelper.transcriptionChunksToCleanString(
              this.transcriptionChunksInProcess,
            ),
          });
          this.state.set('inactive');
          break;
        case 'aborting':
        case 'error':
          this.stopAzureRecognizer();
          this.state.set('inactive');
          break;
      }

      untracked(() => {
        if (this.ffSttDebugLog()) {
          console.log('STT Debug Log:', {
            state: this.state(),
            transcriptionChunksInProcess: this.transcriptionChunksInProcess,
            recognizer: this.recognizer ?? 'n/a',
          });
        }
      });
    });
  }

  /**
   * Start the service by initializing the recognizer and starting the continuous recognition.
   */
  // eslint-disable-next-line @typescript-eslint/require-await
  public async start(): Promise<void> {
    this.state.set('starting');
  }
  /**
   * Finalize the transcription by stopping the continuous recognition.
   */
  public finalize() {
    this.state.set('finalizing');
  }

  public abort() {
    this.state.set('aborting');
  }

  private async initializeAudioStreaming() {
    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
    } catch (error) {
      console.error('Error initializing audio streaming:', error);
      Sentry.captureException(error);
    }
  }

  // ------------------------------
  // Azure AI
  // ------------------------------
  private async initializeRecognizer() {
    try {
      const apiKey = await this.azureApiKeyService.getApiKey();
      const region = environment.azureSpeechToTextRegion;

      if (!region) {
        throw new Error('Region for Azure Speech-to-text not defined');
      }

      const speechConfig = SpeechConfig.fromAuthorizationToken(apiKey, region);
      const audioConfig = this.mediaStream
        ? AudioConfig.fromStreamInput(this.mediaStream)
        : AudioConfig.fromDefaultMicrophoneInput();

      const languageConfig = AutoDetectSourceLanguageConfig.fromLanguages([
        'en-US',
        'en-CA',
        'fr-CA',
      ]);
      speechConfig.speechRecognitionLanguage = 'en-US';

      this.recognizer = SpeechRecognizer.FromConfig(
        speechConfig,
        languageConfig,
        audioConfig,
      );

      this.recognizer.recognized = this.handleRecognized.bind(this);
      this.recognizer.canceled = this.handleCanceled.bind(this);
      this.recognizer.sessionStopped = this.handleSessionStopped.bind(this);
    } catch (error) {
      console.error(error);
      console.error('Error initializing recognizer for Azure speech-to-text:');
      throw error;
    }
  }

  private readonly handleRecognized = (
    recognizer: SpeechSDKType.Recognizer,
    recognizedEvent: SpeechSDKType.SpeechRecognitionEventArgs,
  ) => {
    const transcript = recognizedEvent.result.text;
    const isFinalTranscript =
      recognizedEvent.result.reason === ResultReason.RecognizedSpeech ||
      recognizedEvent.result.reason === ResultReason.RecognizedKeyword;
    // Ignore callbacks from an old recognizer instance (including callbacks
    // that arrive after the service has been torn down and this.recognizer is null).
    // eslint-disable-next-line sonarjs/different-types-comparison
    if (recognizer !== this.recognizer) {
      if (this.ffSttDebugLog()) {
        console.warn(
          'STT Debug Log - Recognizer callback from an old recognizer instance. Ignoring transcription.',
          {
            reason: ResultReason[recognizedEvent.result.reason],
            text: recognizedEvent.result.text,
          },
        );
      }
      return;
    }

    if (this.ffSttDebugLog()) {
      console.log('STT Debug Log - Transcript result:', {
        transcript,
        isFinalTranscript,
        reason: ResultReason[recognizedEvent.result.reason],
      });
    }

    // Ignore if the transcript is not final
    if (!transcript || !isFinalTranscript) {
      return;
    }
    // Ignore if the state is not transcripting or finalizing
    if (this.state() !== 'transcripting' && this.state() !== 'finalizing') {
      if (this.ffSttDebugLog()) {
        console.log(
          'STT Debug Log - Recognizer callback not in transcripting or finalizing state. Ignoring transcription.',
          {
            state: this.state(),
            reason: ResultReason[recognizedEvent.result.reason],
            text: recognizedEvent.result.text,
          },
        );
      }
      return;
    }

    this.transcriptionChunksInProcess.push(transcript);

    this.streamResult.emit({
      transcript: fromHelper.transcriptionChunksToCleanString(
        this.transcriptionChunksInProcess,
      ),
      isFinal: isFinalTranscript,
      original: recognizedEvent,
    });
  };

  /**
   * If this is called, it means an error occurred during recognition.
   */
  private readonly handleCanceled = (
    recognizer: SpeechSDKType.Recognizer,
    canceledEvent: SpeechSDKType.SpeechRecognitionCanceledEventArgs,
  ) => {
    Sentry.captureEvent({
      message: 'Azure speech-to-text recognition canceled',
      level: 'error',
      extra: {
        reason: CancellationReason[canceledEvent.reason],
        errorCode: CancellationErrorCode[canceledEvent.errorCode],
        errorDetails: canceledEvent.errorDetails,
      },
    });
    this.abort();
  };

  private readonly handleSessionStopped = () => {
    console.log('Session stopped for Azure speech-to-text.');
  };

  private startContinuousRecognition(): Promise<void> {
    console.log('Starting continuous recognition for Azure speech-to-text');
    return new Promise((resolve, reject) => {
      if (!this.recognizer) {
        console.error('Recognizer not initialized for Azure speech-to-text');
        return reject(
          new Error('Recognizer not initialized for Azure speech-to-text'),
        );
      }

      this.recognizer.startContinuousRecognitionAsync(
        () => {
          console.log('Azure speech-to-text recognition started.');
          this.state.set('transcripting');
          resolve();
        },
        (err) => {
          console.error(
            'Error starting continuous recognition for Azure speech-to-text. Error:',
            err,
          );
          this.abort();
          reject(new Error(err));
        },
      );
    });
  }

  private stopAzureRecognizer() {
    if (this.recognizer) {
      this.recognizer.stopContinuousRecognitionAsync(
        () => {
          console.log(
            'Continuous recognition stopped for Azure speech-to-text.',
          );
          /**
           * This makes sure we properly emit the transcription completed event.
           */
          if (this.state() === 'finalizing') {
            this.state.set('completed');
          }
        },
        (err) => {
          console.error(
            'Error stopping continuous recognition for Azure speech-to-text. Error:',
            err,
          );
        },
      );
    }
    this.stopAudioStreaming();
  }

  private stopAudioStreaming() {
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => {
        track.stop();
      });
      this.mediaStream = undefined;
    }
  }

  ngOnDestroy() {
    this.abort();
  }
}
